import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Leistungen', href: '#leistungen' },
  { label: 'Referenzen', href: '#referenzen' },
  { label: 'Über uns', href: '#ueber-uns' },
  { label: 'Kontakt', href: '#kontakt' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/' + href;
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-[#F8F5F0]/96 backdrop-blur-md shadow-sm border-b border-[#EDE8E0]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex flex-col group" data-testid="navbar-logo">
            <span className="font-cormorant text-xl font-semibold text-[#2A2A2A] group-hover:text-[#8B7355] transition-colors duration-200 leading-tight">
              Schulz & Gojowy
            </span>
            <span className="text-[10px] text-[#8B7355] tracking-[0.18em] uppercase font-outfit">
              Gardinen-Atelier Berlin
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleNavClick(link.href)}
                className="text-xs tracking-[0.15em] uppercase font-outfit text-[#2A2A2A]/75 hover:text-[#8B7355] transition-colors duration-200 py-1"
              >
                {link.label}
              </button>
            ))}
            <a
              href="tel:+493043115080"
              data-testid="navbar-phone-btn"
              className="flex items-center gap-2 bg-[#8B7355] text-white px-5 py-2.5 text-xs tracking-wide uppercase font-outfit hover:bg-[#6B5440] transition-colors duration-200"
            >
              <Phone size={13} />
              030 / 4 31 15 80
            </a>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            className="md:hidden p-2 text-[#2A2A2A]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menü öffnen"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-[#F8F5F0] border-t border-[#EDE8E0]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left text-sm uppercase tracking-[0.15em] font-outfit text-[#2A2A2A] py-3 border-b border-[#EDE8E0] hover:text-[#8B7355] transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="tel:+493043115080"
                className="flex items-center justify-center gap-2 bg-[#8B7355] text-white px-5 py-3.5 text-xs uppercase tracking-wide font-outfit mt-4"
              >
                <Phone size={13} />
                030 / 4 31 15 80
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
